/**
 * Helpers for the report "Chat with this report" buttons. Mirrors the docs
 * `build-doc-markdown` discipline: a short, neutral prompt that links to a
 * public Markdown URL rather than inlining the report (so it survives the
 * assistants' `?q=` length limits). The Markdown itself is served by the
 * `/api/reports-markdown/*` routes.
 */

export { AI_ASSISTANTS, type AiAssistant } from "@shared/lib/ai-assistants";

export type ReportType = "ai-visibility" | "site-audit";

export interface ReportAskPromptInput {
  reportType: ReportType;
  /** Human label for the subject — a brand name or domain. */
  subjectLabel: string;
  /** Absolute URL serving the report as Markdown. */
  markdownUrl: string;
}

const REPORT_LABELS: Record<ReportType, string> = {
  "ai-visibility": "AI Visibility",
  "site-audit": "AI Site Readiness",
};

/**
 * Short, ethical pre-filled prompt for AI assistants. Kept brief so it survives
 * the assistants' `?q=` length limits, and links to the Markdown URL rather
 * than inlining the report.
 */
export function buildReportAskPrompt({
  reportType,
  subjectLabel,
  markdownUrl,
}: ReportAskPromptInput): string {
  const label = REPORT_LABELS[reportType];
  return [
    `I'm reviewing my Spyglasses ${label} report for ${subjectLabel}. Spyglasses is an AI Visibility platform for marketing and PR firms.`,
    `Here is the report as Markdown: ${markdownUrl}`,
    `Please read it, summarize the key findings, and help me prioritize what to fix first. If a section links to more detailed Markdown, fetch it when you need to drill in.`,
  ].join("\n\n");
}
